import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { agenteInventarioAPI } from '../../services/api';
import { Loader2, Bot, User as UserIcon, LifeBuoy } from 'lucide-react';
import AsistenciaBadge from './AsistenciaBadge';

// P13B — Esqueleto del Agente 1: conversación DETERMINISTA (guion cerrado, sin IA ni LLM).
export const AgenteInventario = ({ asesores, seccionActual, onCerrar, onGuardado }) => {
  const { toast } = useToast();
  const [guion, setGuion] = useState(null);
  const [asesorId, setAsesorId] = useState('');
  const [sesion, setSesion] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [valor, setValor] = useState('');
  const [cargando, setCargando] = useState(false);
  const finRef = useRef(null);

  useEffect(() => {
    agenteInventarioAPI.guion().then(setGuion).catch(() => {});
  }, []);
  useEffect(() => { finRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensajes]);

  const disponibles = seccionActual
    ? asesores.filter((a) => a.seccion_id === seccionActual) : asesores;
  const paso = sesion?.paso_actual || null;

  const aplicar = (d, textoUsuario) => {
    setSesion(d);
    setValor('');
    setMensajes((m) => [
      ...m,
      ...(textoUsuario ? [{ de: 'humano', texto: textoUsuario }] : []),
      ...(d.mensaje_agente ? [{ de: 'agente', texto: d.mensaje_agente }] : []),
    ]);
  };

  const error = (e) => toast({ title: 'Acción rechazada', description: e.message,
    variant: 'destructive' });

  const iniciar = async () => {
    setCargando(true);
    try {
      const d = await agenteInventarioAPI.crearSesion({ asesor_id: asesorId, canal: 'web_dev',
        consentimiento: true, origen: 'agente_inventario_dev' });
      setMensajes([{ de: 'agente', texto: guion?.presentacion || '' }]);
      aplicar(d, null);
    } catch (e) { error(e); } finally { setCargando(false); }
  };

  const responder = async (omitir) => {
    if (!paso) return;
    setCargando(true);
    try {
      const enviado = omitir ? null : valor;
      const d = await agenteInventarioAPI.responder(sesion.sesion_id, {
        paso: paso.clave, valor: enviado,
        idempotency_key: `${sesion.sesion_id}:${paso.clave}:${sesion.interacciones}`,
      });
      aplicar(d, omitir ? '(omitido)' : String(valor));
    } catch (e) { error(e); } finally { setCargando(false); }
  };

  const confirmar = async () => {
    setCargando(true);
    try {
      const d = await agenteInventarioAPI.confirmar(sesion.sesion_id, {
        idempotency_key: `${sesion.sesion_id}:confirmar`,
      });
      toast({ title: 'Propiedad registrada',
        description: `${d.propiedad?.nombre} (${d.propiedad?.referencia}).` });
      setSesion(d.sesion);
      setMensajes((m) => [...m, { de: 'agente',
        texto: `Listo: el inmueble quedó en el inventario con referencia ${d.propiedad?.referencia}.` }]);
      onGuardado();
    } catch (e) { error(e); } finally { setCargando(false); }
  };

  const pedirHumano = async () => {
    setCargando(true);
    try {
      const d = await agenteInventarioAPI.handoff(sesion.sesion_id,
        { motivo: 'El asesor solicita apoyo humano para completar la captura' });
      aplicar(d, 'Necesito apoyo humano');
    } catch (e) { error(e); } finally { setCargando(false); }
  };

  return (
    <Dialog open onOpenChange={onCerrar}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"
                     data-testid="agente-inventario-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" /> Agente gestor de inventario
            <Badge className="bg-slate-100 text-slate-700">esqueleto determinista</Badge>
            <AsistenciaBadge />
          </DialogTitle>
        </DialogHeader>

        {!sesion && (
          <div className="space-y-3" data-testid="agente-inventario-inicio">
            <p className="text-sm text-gray-600">{guion?.presentacion}</p>
            <select value={asesorId} onChange={(e) => setAsesorId(e.target.value)}
                    className="border rounded-lg px-3 py-2 text-sm bg-white w-full"
                    data-testid="agente-inventario-asesor">
              <option value="">Asesor responsable del inmueble…</option>
              {disponibles.map((a) => (
                <option key={a.asesor_id} value={a.asesor_id}>{a.nombre}</option>
              ))}
            </select>
            <Button onClick={iniciar} disabled={!asesorId || cargando}
                    data-testid="agente-inventario-iniciar-btn">
              {cargando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Iniciar conversación
            </Button>
          </div>
        )}

        {sesion && (
          <div className="space-y-4" data-testid="agente-inventario-conversacion">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge className="bg-indigo-100 text-indigo-800"
                     data-testid="agente-inventario-estado">{sesion.estado}</Badge>
              <span className="text-gray-500" data-testid="agente-inventario-progreso">
                {sesion.progreso.respondidos}/{sesion.progreso.total_pasos} datos ·
                {' '}{sesion.progreso.requeridos_pendientes.length} obligatorio(s) pendiente(s)
              </span>
              {sesion.handoff_humano && (
                <Badge className="bg-amber-100 text-amber-800"
                       data-testid="agente-inventario-handoff">apoyo humano solicitado</Badge>
              )}
            </div>

            <div className="border rounded-xl p-3 space-y-3 max-h-72 overflow-y-auto bg-gray-50">
              {mensajes.filter((m) => m.texto).map((m, i) => (
                <div key={i} className={`flex gap-2 text-sm ${m.de === 'humano'
                  ? 'justify-end text-right' : ''}`}>
                  {m.de === 'agente' && <Bot className="h-4 w-4 mt-1 text-indigo-600 shrink-0" />}
                  <span className={`rounded-2xl px-3 py-2 max-w-[80%] ${m.de === 'humano'
                    ? 'bg-indigo-600 text-white' : 'bg-white border'}`}>{m.texto}</span>
                  {m.de === 'humano' && <UserIcon className="h-4 w-4 mt-1 text-gray-400 shrink-0" />}
                </div>
              ))}
              <div ref={finRef} />
            </div>

            {paso && (
              <div className="flex flex-wrap items-center gap-2">
                {paso.opciones
                  ? (
                    <select value={valor} onChange={(e) => setValor(e.target.value)}
                            className="border rounded-lg px-3 py-2 text-sm bg-white flex-1"
                            data-testid="agente-inventario-valor">
                      <option value="">Elige una opción…</option>
                      {paso.opciones.map((o) => (
                        <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  )
                  : (
                    <Input value={valor} className="flex-1"
                           type={['entero', 'decimal', 'moneda'].includes(paso.tipo)
                             ? 'number' : 'text'}
                           placeholder="Tu respuesta"
                           onChange={(e) => setValor(e.target.value)}
                           data-testid="agente-inventario-valor" />
                  )}
                <Button onClick={() => responder(false)} disabled={cargando || valor === ''}
                        data-testid="agente-inventario-responder-btn">
                  {cargando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Responder
                </Button>
                {!paso.requerido && (
                  <Button variant="outline" onClick={() => responder(true)} disabled={cargando}
                          data-testid="agente-inventario-omitir-btn">
                    Omitir
                  </Button>
                )}
                <Button variant="ghost" onClick={pedirHumano} disabled={cargando}
                        data-testid="agente-inventario-handoff-btn">
                  <LifeBuoy className="h-4 w-4 mr-2" />Apoyo humano
                </Button>
              </div>
            )}

            {sesion.estado === 'lista_para_confirmar' && (
              <Button onClick={confirmar} disabled={cargando}
                      data-testid="agente-inventario-confirmar-btn">
                {cargando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Confirmar alta en el inventario
              </Button>
            )}

            {sesion.estado === 'publicada' && (
              <Button variant="outline" onClick={onCerrar}
                      data-testid="agente-inventario-cerrar-btn">
                Cerrar
              </Button>
            )}

            <p className="text-xs text-gray-500">
              Guion cerrado y determinista: sin IA generativa, sin claves de modelos, sin WhatsApp,
              sin imágenes y sin envíos externos. El inmueble se crea en la sección del asesor con
              auditoría.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AgenteInventario;
